import React, { Suspense } from 'react';

import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider } from 'react-oidc-context';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';

import { getAuthProviderConfig } from 'auth/auth-provider-config';
import { NetworkContext } from 'networking/network-context';
import { AppErrorBoundary } from 'shared-components/app-error-boundary';
import { PageLoadingSpinner } from 'shared-components/page-loading-spinner';
import { StyledToastContainer } from 'shared-components/styled-toast-container';
import { AppThemeProvider } from 'theme';

import { AppRoutes } from './app-routes';

interface RouteTreeErrorBoundaryProps {
  children: React.ReactNode;
}

// location.key is passed as a resetKey PROP, never as a React key: every
// navigation (path, query-only, hash-only, back/forward) recovers a crashed
// view, while a healthy route tree is never remounted by navigation.
export const RouteTreeErrorBoundary: React.FC<RouteTreeErrorBoundaryProps> = ({
  children,
}) => {
  const { key } = useLocation();

  return <AppErrorBoundary resetKey={key}>{children}</AppErrorBoundary>;
};

const Root: React.FC = () => (
  <Suspense
    fallback={
      <PageLoadingSpinner
        data-testid="synmatch-ai-app-loading"
        style={{ height: 500 }}
      />
    }
  >
    <AppThemeProvider>
      <AuthProvider {...getAuthProviderConfig()}>
        <Router>
          <RouteTreeErrorBoundary>
            <NetworkContext>
              <AppRoutes />
            </NetworkContext>
          </RouteTreeErrorBoundary>
        </Router>
      </AuthProvider>

      <StyledToastContainer />
    </AppThemeProvider>
  </Suspense>
);

export default Root;
